import classNames from 'classnames'
import { ElDialog } from 'element-plus'
import { useResponsive } from 'store'
import { omit } from 'utils'
import { defineComponent, type StyleValue } from 'vue'
import './HocElDialog.scss'

type Placement = 'bottom' | 'right' | 'center'

type HocElDialogProps = InstanceType<typeof ElDialog>['$props'] & {
  style?: StyleValue
  placement?: Placement
  class?: string
  offsetTop?: number
  footer?: null
}

export const HocElDialog = defineComponent(
  (_: HocElDialogProps, { slots, attrs }) => {
    const props = attrs as HocElDialogProps
    const responsive = useResponsive()

    const placement = computed<Placement>(() => props.placement || 'bottom')

    const dialogWidth = computed(() => {
      if (responsive.isMobile) return '100%'
      return props.width
    })

    const dialogTransition = computed(() => {
      if (responsive.isMobile && placement.value === 'bottom') return 'dialog-slide-bottom-to-top'
      if (responsive.isMobile && placement.value === 'right') return 'dialog-slide-right-to-left'
      return 'dialog-fade'
    })

    const classNameList = computed(() =>
      classNames('hoc-el-dialog', props.class, `placement-${placement.value}`, !slots.footer && 'no-footer', props.offsetTop ? 'has-offset-top' : null),
    )

    const style = computed<StyleValue>(() => {
      if (props.offsetTop) {
        return { '--hoc-el-dialog-offset-top': `${props.offsetTop}px` } as StyleValue
      }
      return undefined as unknown as StyleValue
    })

    return () => (
      <ElDialog
        {...(omit(props, ['offsetTop']) as Partial<HocElDialogProps>)}
        width={dialogWidth.value}
        transition={dialogTransition.value}
        class={classNameList.value}
        style={style.value}
      >
        {{
          header: () => slots.header?.(),
          default: () => slots.default?.(),
          footer: () => slots.footer?.(),
        }}
      </ElDialog>
    )
  },
  {
    name: 'HocElDialog',
    inheritAttrs: false,
  },
)

export const HocElDialogFooterLayout = defineComponent({
  name: 'HocElDialogFooterLayout',
  setup(_props, { slots }) {
    return () => <div class="hoc-el-dialog-footer-layout">{slots.default?.()}</div>
  },
})
